import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, notification, Space } from "antd";
import { useForm } from "antd/es/form/Form";

import styles from "../Extras.module.scss";

import { Extra } from "@/root/models/extra";
import { CreateExtraPayload, ExtrasApi } from "@/api/admin/extras";
import ExtraForm from "./ExtraForm";
import AddExtraModal from "./AddExtraModal";

interface ExtraProps {
  extra: Extra;
  onDelete: (extra: Extra) => void;
  onEdit: (extra: Extra) => void;
  onAdd: (extra: Extra) => void;
}

const { confirm } = Modal;

const ExtraItem = ({ extra, onDelete, onEdit, onAdd }: ExtraProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const handleEdit = async (values: CreateExtraPayload) => {
    try {
      setEditLoading(true);
      setEditError("");
      const newExtra = await ExtrasApi.edit(extra.extraUUID, {
        ...values,
        parentExtraUUID: extra.parentExtraUUID,
      });
      onEdit(newExtra);
      setEditModal(false);
    } catch (e: any) {
      setEditError(e.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await ExtrasApi.delete(extra.extraUUID);
      onDelete(extra);
    } catch (e: any) {
      notification.error({
        message: e.message,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const editSubmit = () => {
    form.submit();
  };

  const openEditModal = () => {
    form.setFieldsValue({
      name: extra.name,
      isGroup: extra.isGroup,
    });
    setEditModal(true);
  };

  const openAddModal = () => {
    setAddModal(true);
  };

  const handleDeleteButtonClick = () => {
    confirm({
      title: t("Підтвердити видалення"),
      okText: t("Видалити"),
      okType: "danger",
      cancelText: t("Скасувати"),
      onOk: handleDelete,
    });
  };

  const handleAddExtra = (extra: Extra) => {
    onAdd(extra);
    setAddModal(false);
  };

  return (
    <div className={styles.extra}>
      <span>{extra.name}</span>
      <Space className="tree-actions">
        {extra.isGroup && extra.level < 5 && (
          <Button size="small" onClick={openAddModal}>
            {t("Додати")}
          </Button>
        )}
        <Button size="small" onClick={openEditModal}>
          {t("Редагувати")}
        </Button>
        <Button
          loading={deleteLoading}
          size="small"
          onClick={handleDeleteButtonClick}
        >
          {t("Видалити")}
        </Button>
      </Space>
      <Modal
        maskClosable={false}
        okText={t("Зберегти")}
        title={
          <div style={{ marginBottom: 20 }}>
            {t("Редагування додаткової роботи або послуги")}
          </div>
        }
        open={editModal}
        onCancel={() => setEditModal(false)}
        onOk={editSubmit}
        okButtonProps={{ loading: editLoading }}
      >
        <ExtraForm
          form={form}
          handleSubmit={handleEdit}
          error={editError}
          groupDisabled={extra.level === 4}
        />
      </Modal>
      {extra.isGroup && extra.level < 5 && (
        <AddExtraModal
          extra={extra}
          open={addModal}
          onCancel={() => setAddModal(false)}
          onAdd={handleAddExtra}
        />
      )}
    </div>
  );
};

export default ExtraItem;
